/**
 * Pré-enregistrement — l'abonnement à 4,99 €/mois.
 *
 * Trois promesses, dans l'ordre où elles comptent : voir avant, ne rien rater,
 * être vu. L'écran sert autant à vendre (pour qui n'est pas abonné) qu'à
 * piloter (pour qui l'est) — d'où deux états nettement séparés.
 */
import { useEffect, useState } from 'react';
import { Check, Clock, Copy, Eye, Sparkles } from 'lucide-react';
import { appelerApi, envoyer } from '../../reseau.ts';
import { Badge, Btn, Card, PageTitle, SectionLabel } from '../../admin/ui.tsx';
import {
  dateLongue,
  eur,
  euros,
  type DescriptionFormule,
  type EtatAbonnement,
  type EvenementAvantPremiere,
  type ModeAbonnement,
} from './types.ts';

const MODES: { valeur: ModeAbonnement; titre: string; texte: string }[] = [
  {
    valeur: 'auto',
    titre: 'Inscrivez-moi d’office',
    texte:
      'Dès qu’un événement attire mon public cible, vous m’inscrivez et vous le posez dans mon agenda. Je peux toujours me désinscrire.',
  },
  {
    valeur: 'interesse',
    titre: 'Notez-moi « très intéressé »',
    texte:
      'Sans m’engager : l’événement entre dans mon agenda en provisoire, et l’organisateur sait que je suis un candidat sérieux.',
  },
  {
    valeur: 'libre',
    titre: 'Laissez-moi choisir',
    texte: 'Prévenez-moi 24 h avant tout le monde, et je décide événement par événement.',
  },
];

export function PreEnregistrement() {
  const [etat, setEtat] = useState<EtatAbonnement | null>(null);
  const [avant, setAvant] = useState<EvenementAvantPremiere[] | null>(null);
  const [enCours, setEnCours] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const charger = async () => {
    const r = await appelerApi<EtatAbonnement>('/api/preenregistrement/moi');
    setEtat(r.donnees ?? null);
    if (r.donnees?.actif) {
      const a = await appelerApi<{ evenements: EvenementAvantPremiere[] }>('/api/preenregistrement/avant-premiere');
      setAvant(a.donnees?.evenements ?? []);
    }
  };
  useEffect(() => {
    void charger();
  }, []);

  const souscrire = async () => {
    setEnCours(true);
    setMessage(null);
    const r = await envoyer<{ url: string }>('/api/preenregistrement/souscrire', {});
    setEnCours(false);
    if (r.donnees?.url) location.assign(r.donnees.url);
    else setMessage(r.erreur ?? 'Paiement indisponible pour l’instant.');
  };

  if (etat === null) return <p className="text-[14px] text-fumee">Chargement…</p>;

  return (
    <>
      <PageTitle
        title={
          <>
            Pré-<em className="text-match-gradient font-serif italic font-medium">enregistrement</em>
          </>
        }
        subtitle="Voir les événements avant tout le monde, ne rater aucun de ceux qui comptent, et apparaître dans les annonces devant votre audience."
      />

      {!etat.actif && <Argumentaire prix={etat.prix_centimes} enCours={enCours} onSouscrire={() => void souscrire()} message={message} />}

      {etat.actif && etat.abonnement && (
        <>
          <Card className="mb-6">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <SectionLabel>Votre abonnement</SectionLabel>
                <p className="mt-2 text-[15px] text-blanc">
                  {eur(etat.prix_centimes)} par mois ·{' '}
                  {etat.abonnement.statut === 'annule'
                    ? `actif jusqu’au ${etat.abonnement.fin_periode_le ? dateLongue(etat.abonnement.fin_periode_le) : 'la fin de la période'}`
                    : 'actif'}
                </p>
              </div>
              <Badge filled={etat.abonnement.statut !== 'annule'}>
                {etat.abonnement.statut === 'annule' ? 'Résiliation prévue' : 'Actif'}
              </Badge>
            </div>
            {etat.abonnement.statut === 'impaye' && (
              <p className="mt-3 text-[13px]" style={{ color: 'var(--em-erreur)' }}>
                Le dernier prélèvement a échoué. Mettez votre moyen de paiement à jour depuis
                l’e-mail Stripe pour ne pas perdre l’avance de 24 h.
              </p>
            )}
          </Card>

          <Formules formules={etat.formules} actuelle={etat.formule} />
          <Reglages etat={etat} onChange={charger} />
          <Agenda url={etat.url_calendrier} />
          <AvantPremiere evenements={avant} onChange={charger} />
        </>
      )}
    </>
  );
}

/**
 * Les trois formules, la mienne en évidence. Changer applique un prorata
 * Stripe : rien à payer sur-le-champ, l'écran se recharge.
 */
function Formules({ formules, actuelle }: { formules: DescriptionFormule[]; actuelle: string | null }) {
  const [enCours, setEnCours] = useState<string | null>(null);
  const changer = async (formule: string) => {
    setEnCours(formule);
    const r = await envoyer<{ url: string | null }>('/api/preenregistrement/formule', { formule });
    if (r.donnees?.url) location.assign(r.donnees.url);
    else location.reload();
  };
  return (
    <Card className="mb-6">
      <SectionLabel>Votre formule</SectionLabel>
      <p className="mt-2 max-w-[680px] text-[13px] leading-relaxed text-fumee">
        Les formules s’emboîtent : chacune contient la précédente. Les mises en relation
        s’ouvrent à partir de Pro — avec un match offert chaque mois pour tous ; Premium les ouvre toutes, quelle que soit leur valeur.
      </p>
      <div className="mt-4 grid gap-3 lg:grid-cols-3">
        {formules.map((f) => {
          const mienne = f.cle === actuelle;
          return (
            <div key={f.cle} className={`flex flex-col rounded-[10px] border p-4 ${mienne ? 'border-blanc' : 'border-graphite'}`}>
              <div className="flex items-center justify-between">
                <p className="text-[15px] text-blanc">{f.nom}</p>
                {mienne && <Badge filled>Actuelle</Badge>}
              </div>
              <p className="text-match-gradient mt-1 font-serif text-[24px] leading-tight">
                {euros(f.prixCentimes)}
                <span className="font-sans text-[13px] text-fumee"> /mois</span>
              </p>
              <ul className="mt-3 flex flex-1 flex-col gap-1.5">
                {f.avantages.map((a) => (
                  <li key={a} className="flex items-start gap-2 text-[13px] leading-relaxed text-fumee">
                    <Check size={13} strokeWidth={1.5} className="mt-1 shrink-0" /> {a}
                  </li>
                ))}
              </ul>
              {!mienne && (
                <Btn className="mt-4" onClick={() => void changer(f.cle)} disabled={enCours !== null}>
                  {enCours === f.cle ? 'Un instant…' : `Passer à ${f.nom}`}
                </Btn>
              )}
            </div>
          );
        })}
      </div>
    </Card>
  );
}

function Argumentaire({ prix, enCours, onSouscrire, message }: { prix: number; enCours: boolean; onSouscrire: () => void; message: string | null }) {
  return (
    <>
      <div className="grid gap-4 lg:grid-cols-3">
        <Card>
          <Clock size={18} strokeWidth={1.5} className="text-fumee" />
          <p className="mt-3 text-[16px] text-blanc">Voir 24 h avant</p>
          <p className="mt-2 text-[14px] leading-relaxed text-fumee">
            Les événements vous arrivent une journée avant leur diffusion publique — avant que les
            places et l’attention ne soient prises.
          </p>
        </Card>
        <Card>
          <Check size={18} strokeWidth={1.5} className="text-fumee" />
          <p className="mt-3 text-[16px] text-blanc">Ne rien rater</p>
          <p className="mt-2 text-[14px] leading-relaxed text-fumee">
            Inscription d’office aux événements qui attirent votre public cible, marque d’intérêt
            sans engagement, ou décision au cas par cas. Votre agenda suit tout seul.
          </p>
        </Card>
        <Card>
          <Eye size={18} strokeWidth={1.5} className="text-fumee" />
          <p className="mt-3 text-[16px] text-blanc">Être vu</p>
          <p className="mt-2 text-[14px] leading-relaxed text-fumee">
            Votre logo dans les annonces — site, e-mails, application et partenaires — et une place
            prioritaire dans la liste des entreprises présentes.
          </p>
        </Card>
      </div>

      <Card className="mt-4">
        <SectionLabel>Comment fonctionne la priorité d’affichage</SectionLabel>
        <p className="mt-2 max-w-[720px] text-[14px] leading-relaxed text-fumee">
          Quand nous annonçons un événement, nous affichons les entreprises présentes. L’ordre
          n’est pas le même pour tout le monde : un visiteur qui cherche le bâtiment voit d’abord
          les entreprises du bâtiment. Vous êtes donc placé devant{' '}
          <span className="text-blanc">votre audience cible</span>, celle que vous déclarez ici —
          et non devant tout le monde, ce qui ne vous apporterait que des clics inutiles et
          rendrait l’annonce illisible.
        </p>
      </Card>

      <Card className="mt-4">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="display-title text-[30px] text-blanc">{eur(prix)}<span className="text-[15px] text-fumee"> / mois</span></p>
            <p className="mt-1 text-[13px] text-fumee">Sans engagement, résiliable en un clic.</p>
          </div>
          <Btn variant="primary" disabled={enCours} onClick={onSouscrire}>
            {enCours ? '…' : 'M’abonner'}
          </Btn>
        </div>
        {message && (
          <p className="mt-3 text-[13px]" role="alert" style={{ color: 'var(--em-erreur)' }}>
            {message}
          </p>
        )}
      </Card>
    </>
  );
}

function Reglages({ etat, onChange }: { etat: EtatAbonnement; onChange: () => Promise<void> }) {
  const [mode, setMode] = useState<ModeAbonnement>(etat.abonnement?.mode ?? 'libre');
  const [secteurs, setSecteurs] = useState((etat.abonnement?.secteurs_cibles ?? []).join(', '));
  const [message, setMessage] = useState<string | null>(null);

  const enregistrer = async (patch: { mode?: ModeAbonnement; secteursCibles?: string[] }) => {
    const r = await envoyer('/api/preenregistrement/reglages', patch, 'PUT');
    setMessage(r.erreur ?? 'Enregistré ✓');
    if (!r.erreur) await onChange();
  };

  const annuler = async () => {
    if (!confirm('Résilier l’abonnement ? Il reste actif jusqu’à la fin de la période déjà payée.')) return;
    const r = await envoyer('/api/preenregistrement/annuler', {});
    setMessage(r.erreur ?? 'Résiliation enregistrée.');
    if (!r.erreur) await onChange();
  };

  return (
    <Card className="mb-4">
      <SectionLabel>Que faire quand un événement vous concerne</SectionLabel>
      <div className="mt-3 flex flex-col gap-3">
        {MODES.map((m) => (
          <label key={m.valeur} className="flex cursor-pointer items-start gap-3">
            <input
              type="radio"
              name="mode"
              checked={mode === m.valeur}
              onChange={() => {
                setMode(m.valeur);
                void enregistrer({ mode: m.valeur });
              }}
              className="mt-1"
            />
            <span>
              <span className="block text-[14px] text-blanc">{m.titre}</span>
              <span className="mt-0.5 block text-[13px] leading-relaxed text-fumee">{m.texte}</span>
            </span>
          </label>
        ))}
      </div>

      <div className="mt-5 border-t border-graphite pt-4">
        <label className="block">
          <span className="label-caps block text-fumee">Les secteurs que vous voulez rencontrer</span>
          <input
            value={secteurs}
            onChange={(e) => setSecteurs(e.target.value)}
            onBlur={() => void enregistrer({ secteursCibles: secteurs.split(',').map((s) => s.trim()).filter(Boolean) })}
            placeholder="Bâtiment, architecture, promotion immobilière"
            className="field-input mt-2 !min-h-[42px] !py-2 !text-sm"
          />
        </label>
        <p className="mt-2 text-[12px] leading-relaxed text-fumee">
          Séparés par des virgules. C’est ce qui décide si un événement « attire votre public
          cible » — et devant quelle audience votre logo remonte. Laissez vide pour suivre tous les
          événements.
        </p>
      </div>

      <div className="mt-5 flex flex-wrap items-center justify-between gap-3 border-t border-graphite pt-4">
        {message && <span className="text-[13px] text-fumee">{message}</span>}
        <button onClick={() => void annuler()} className="ml-auto text-[13px] text-fumee underline hover:text-blanc">
          Résilier l’abonnement
        </button>
      </div>
    </Card>
  );
}

function Agenda({ url }: { url: string | null }) {
  const [copie, setCopie] = useState(false);
  if (!url) return null;
  return (
    <Card className="mb-4">
      <SectionLabel>Votre agenda</SectionLabel>
      <p className="mt-2 max-w-[680px] text-[14px] leading-relaxed text-fumee">
        Collez cette adresse une fois dans Google Agenda (Autres agendas → À partir de l’URL),
        Outlook ou Apple Calendrier : vos événements s’y ajoutent et s’y mettent à jour tout seuls.
        Ceux notés « très intéressé » apparaissent en provisoire, sans bloquer votre créneau.
      </p>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <code className="min-w-0 flex-1 truncate rounded-[6px] border border-graphite px-3 py-2 font-mono text-[12px] text-fumee">{url}</code>
        <Btn
          onClick={() => {
            void navigator.clipboard.writeText(url);
            setCopie(true);
            setTimeout(() => setCopie(false), 2000);
          }}
        >
          <Copy size={13} strokeWidth={1.5} className="mr-1.5 inline" />
          {copie ? 'Copié ✓' : 'Copier'}
        </Btn>
      </div>
    </Card>
  );
}

function AvantPremiere({ evenements, onChange }: { evenements: EvenementAvantPremiere[] | null; onChange: () => Promise<void> }) {
  const [enCours, setEnCours] = useState<string | null>(null);

  const decider = async (id: string, statut: 'inscrit' | 'tres_interesse' | 'decline') => {
    setEnCours(id);
    await envoyer(`/api/preenregistrement/avant-premiere/${id}`, { statut });
    setEnCours(null);
    await onChange();
  };

  return (
    <section className="mt-8">
      <h2 className="text-[18px] font-light text-blanc">Votre avant-première</h2>
      <p className="mb-4 mt-1 max-w-[640px] text-[13px] leading-relaxed text-fumee">
        Les événements qui ne sont pas encore publics — et ceux que vous n’avez pas encore
        tranchés.
      </p>

      {evenements === null && <p className="text-[14px] text-fumee">Chargement…</p>}
      {evenements?.length === 0 && (
        <Card>
          <p className="text-[14px] leading-relaxed text-fumee">
            Rien en avant-première pour l’instant. Dès qu’un organisateur programme la diffusion
            d’un événement, il apparaît ici 24 h avant tout le monde — et vous recevez un e-mail.
          </p>
        </Card>
      )}

      <div className="flex flex-col gap-3">
        {evenements?.map((e) => (
          <Card key={e.id}>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="flex flex-wrap items-center gap-2 text-[15px] text-blanc">
                  {e.nom}
                  {!e.deja_public && (
                    <Badge filled>
                      <Sparkles size={11} strokeWidth={1.5} className="mr-1 inline" />
                      Public dans {e.public_dans_heures} h
                    </Badge>
                  )}
                  {e.ma_decision === 'inscrit' && <Badge>Inscrit</Badge>}
                  {e.ma_decision === 'tres_interesse' && <Badge>Très intéressé</Badge>}
                </p>
                <p className="mt-1 text-[13px] text-fumee">
                  {dateLongue(e.debut_le)}
                  {e.lieu_ville ? ` · ${e.lieu_ville}` : ''} · {e.organisation_nom} · {e.participants} inscrits
                </p>
                <p className="mt-2 max-w-[620px] text-[13px] leading-relaxed text-blanc">{e.ciblage.raison}</p>
              </div>
              {e.ma_decision !== 'inscrit' && (
                <span className="flex flex-wrap gap-2">
                  <Btn variant="primary" disabled={enCours === e.id} onClick={() => void decider(e.id, 'inscrit')}>
                    M’inscrire
                  </Btn>
                  {e.ma_decision !== 'tres_interesse' && (
                    <Btn disabled={enCours === e.id} onClick={() => void decider(e.id, 'tres_interesse')}>
                      Très intéressé
                    </Btn>
                  )}
                  <Btn disabled={enCours === e.id} onClick={() => void decider(e.id, 'decline')}>
                    Passer
                  </Btn>
                </span>
              )}
            </div>
          </Card>
        ))}
      </div>
    </section>
  );
}
